import { Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
  LoadingController,
  ModalController,
  NavController,
  NavParams,
} from "ionic-angular";
import { debounceTime, distinctUntilChanged, take } from "rxjs/operators";
import { AuthService } from "../../app/auth/auth.service";
import SortOptions from "../../models/content-wrapper/sort-options";
import LookupValue from "../../models/patient-chart/lookupValue";
import CompanyResourceSummary from "../../models/resource-management/company-resource-summary";
import CompanyResourceType from "../../models/resource-management/company-resource-type.enum";
import ProfessionalProvider from "../../models/resource-management/professional-provider";
import VolunteerMatchTerm from "../../models/resource-management/volunteers/volunteer-match-term";
import { CompanyResourcesContactPage } from "../../pages/company-resources/company-resources-contact/company-resources-contact";
import { CompanyResourcesEditPage } from "../../pages/company-resources/company-resources-edit/company-resources-edit";
import { CompanyTeamsService } from "../../providers/company-teams-service/company-teams-service";
import { NavBarServiceProvider } from "../../providers/nav-bar-service/nav-bar-service";
import { ResourceManagementService } from "../../providers/resource-management/resource-management.service";
import { StateManagementService } from "../../providers/state-management/state-management.service";
import { VolunteerManagementServiceProvider } from "../../providers/volunteer-management-service/volunteer-management-service";
import SelectOption from "../common/multi-select-dropdown/selection-option";

@Component({
  selector: "company-resources-grid",
  templateUrl: "company-resources-grid.html",
})
export class CompanyResourcesGridComponent implements OnInit {
  public featureToggleProviderFlatUI: boolean;

  private _cardHeader: string = "Providers";
  private _contactType: CompanyResourceType;
  static LIMIT: number = 10;
  private spinnerCounter: number = 0;
  private totalProviders: number;
  private spinner: any = null;
  private providerSummaries: CompanyResourceSummary[] = [];
  sortOptions: SortOptions = new SortOptions("last_name", "asc");
  private currentPage: number;
  private totalPageCount: number;
  searchTerm: string;
  private selectedProviders: Map<number, ProfessionalProvider> = new Map<
    number,
    ProfessionalProvider
  >();
  private _credentialTypes: LookupValue[];
  private _volunteerTypes: LookupValue[];
  private _hasNonProviderCliniciansEnabled: boolean = false;
  private _hasVolunteersEnabled: boolean = false;
  private _hasProviderEnabled: boolean = false;
  private _languages: LookupValue[] = [];
  private _professionalRelations: LookupValue[] = [];
  private _volunteerActivityCategories: VolunteerMatchTerm[] = [];
  private _volunteerMatchTerms: VolunteerMatchTerm[] = [];
  private _volunteerMatchTermItems: SelectOption[] = null;
  private _languageSelectItems: SelectOption[] = null;
  private selectedSkillsFilter: string[] = [];
  private selectedLocations: string[] = [];
  private selectedLanguages: number[] = [];
  private languageLookup: any;
  private query: string = "";
  gridForm: FormGroup;

  constructor(
    private authSvc: AuthService,
    public navCtrl: NavController,
    public navParams: NavParams,
    public modalController: ModalController,
    public resourceManagementService: ResourceManagementService,
    public volunteerManagementService: VolunteerManagementServiceProvider,
    private loader: LoadingController,
    private stm: StateManagementService,
    private fb: FormBuilder,
    private navBarSvc: NavBarServiceProvider,
    private companyTeamsService: CompanyTeamsService
  ) {
    this.featureToggleProviderFlatUI =
      this.navBarSvc.featureToggleOn("Provider Flat UI");
    if (this.featureToggleProviderFlatUI === undefined) {
      this.featureToggleProviderFlatUI = false;
    }

    this.gridForm = fb.group({
      skillsInterests: "",
      languages: "",
      locationPreferences: fb.control("", [Validators.pattern("^[0-9]{5}$")]),
      query: "",
    });

    this.gridForm
      .get("skillsInterests")
      .valueChanges.subscribe((x: string[]) => {
        this.selectedSkillsFilter = x;
        this.loadProfessionalProviders(0);
      });

    this.gridForm
      .get("query")
      .valueChanges.pipe(
        //filter(Boolean),
        debounceTime(1000),
        distinctUntilChanged()
      )
      .subscribe((x: string) => {
        this.query = x;
        this.loadProfessionalProviders(0);
      });

    this.gridForm
      .get("locationPreferences")
      .valueChanges.pipe(
        //filter(Boolean),
        debounceTime(1000),
        distinctUntilChanged()
      )
      .subscribe((x: string) => {
        this.selectedLocations = [x];
        this.loadProfessionalProviders(0);
      });

    this.gridForm
      .get("languages")
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((x: string[]) => {
        this.selectedLanguages = x.map((y) => {
          return this.languageLookup[y];
        });
        this.loadProfessionalProviders(0);
      });
  }

  ngOnInit(): void {
    this.loadProfessionalProviders(0);
  }

  addCompanyResource() {
    this.navCtrl.push(CompanyResourcesContactPage, {
      languagesSelectItems: this._languageSelectItems,
      contactType: this._contactType,
      parent: this,
      isNew: true,
    });
  }

  openCompanyResource(p: CompanyResourceSummary) {
    if (this.featureToggleProviderFlatUI) {
      this.increaseSpinnerCount(`Editing ${this.getContactType()} ...`);
      this.resourceManagementService
        .getCredentialedResource(p.id)
        .pipe(take(1))
        .subscribe(
          (x) => {
            this.navCtrl.push(CompanyResourcesContactPage, {
              provider: x,
              languagesSelectItems: this._languageSelectItems,
              volunteerTypes: this._volunteerTypes,
              parent: this,
              isNew: false,
            });
            this.decreaseSpinnerCount();
          },
          (error) => {
            this.decreaseSpinnerCount();
          }
        );
    }
  }

  onPageChange(selectedPage: number) {
    this.loadProfessionalProviders(selectedPage);
  }

  loadProfessionalProviders(selectedPage: number): void {
    this.increaseSpinnerCount(`Loading ${this.getContactType()} ...`);
    if (this.contactType === 5) {
      this.volunteerManagementService
        .getVolunteerSummaries(
          selectedPage,
          CompanyResourcesGridComponent.LIMIT,
          this.query,
          this.sortOptions,
          this.selectedSkillsFilter,
          this.selectedLocations,
          this.selectedLanguages
        )
        .pipe()
        .take(1)
        .subscribe(
          (x) => {
            this.providerSummaries = x.content;
            this.totalPageCount = x.totalPages;
            this.totalProviders = x.totalElements;
            this.currentPage = x.pageable.pageNumber;
            this.decreaseSpinnerCount();
          },
          (error) => {
            this.decreaseSpinnerCount();
          }
        );
    } else {
      this.resourceManagementService
        .getCompanyResourceSummaries(
          selectedPage,
          CompanyResourcesGridComponent.LIMIT,
          this.query,
          this.sortOptions,
          this.contactType
        )
        .pipe()
        .take(1)
        .subscribe(
          (x) => {
            this.providerSummaries = x.content;
            this.totalPageCount = x.totalPages;
            this.totalProviders = x.totalElements;
            this.currentPage = x.pageable.pageNumber;
            this.decreaseSpinnerCount();
          },
          (error) => {
            this.decreaseSpinnerCount();
          }
        );
    }
  }

  getContactType() {
    let resource = "providers";
    if (this.contactType === 4) {
      resource = "non-provider clinicians";
    } else if (this.contactType === 5) {
      resource = "volunteers";
    }
    return resource;
  }

  onSearch() {
    this.loadProfessionalProviders(0);
  }

  onSearchCancel() {
    this.searchTerm = "";
    this.loadProfessionalProviders(0);
  }

  arrowClick(p: CompanyResourceSummary) {
    p.isSelected = !p.isSelected;
    if (p.isSelected && p.id) {
      if (!this.selectedProviders.has(p.id)) {
        p.$provider = this.resourceManagementService.getCredentialedResource(
          p.id
        );
      }
    }
  }

  sortBy(selectedCol) {
    this.currentPage = 0;
    if (this.sortOptions.column === selectedCol) {
      this.sortOptions.direction =
        this.sortOptions.direction === "asc" ? "desc" : "asc";
    } else {
      this.sortOptions.column = selectedCol;
      this.sortOptions.direction = "asc";
    }

    this.loadProfessionalProviders(0);
  }

  addProvider() {
    let modal = this.modalController.create(CompanyResourcesEditPage, {
      credentialTypes: this._credentialTypes,
      volunteerTypes: this._volunteerTypes,
      volunteerActivityCategories: this._volunteerActivityCategories,
      contactType: this.contactType,
      hasNonProviderCliniciansEnabled: this.hasNonProviderCliniciansEnabled,
      hasProviderEnabled: this.hasProviderEnabled,
      hasVolunteerEnabled: this.hasVolunteersEnabled,
      languages: this.languages,
    });
    modal
      .present()
      .then((res) => {})
      .catch((err) => {
        console.log("error is", err);
      });
    modal.onDidDismiss(() => {
      this.loadProfessionalProviders(0);
    });
  }

  editClick(p: CompanyResourceSummary) {
    this.increaseSpinnerCount("Editing provider ...");
    this.resourceManagementService
      .getCredentialedResource(p.id)
      .pipe(take(1))
      .subscribe(
        (x) => {
          let modal = this.modalController.create(CompanyResourcesEditPage, {
            provider: x,
            volunteerActivityCategories: this._volunteerActivityCategories,
            credentialTypes: this._credentialTypes,
            volunteerTypes: this._volunteerTypes,
            hasNonProviderCliniciansEnabled:
              this.hasNonProviderCliniciansEnabled,
            hasProviderEnabled: this.hasProviderEnabled,
            hasVolunteerEnabled: this.hasVolunteersEnabled,
            languages: this.languages,
          });
          this.decreaseSpinnerCount();
          modal
            .present()
            .then((res) => {
              //res ? this.modalOpen = true : this.modalOpen = false;
            })
            .catch((err) => {
              console.log("error is", err);
            });
          modal.onDidDismiss(() => {
            this.loadProfessionalProviders(0);
          });
        },
        (error) => {
          this.decreaseSpinnerCount();
        }
      );
  }

  // incremental loader function
  increaseSpinnerCount(message: string = "Loading ...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && !this.spinner) {
      this.spinner = this.loader.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  // decremental loader function
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0 && this.spinner) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  ionViewCanEnter() {
    return this.authSvc.authenticated();
  }

  // region getters and setters
  get contactType(): CompanyResourceType {
    return this._contactType;
  }

  @Input()
  set contactType(value: CompanyResourceType) {
    this._contactType = value;
    if (this._contactType === CompanyResourceType.PROVIDER) {
      this.cardHeader = "Providers";
    } else if (
      this._contactType === CompanyResourceType.NON_PROVIDER_CLINICIAN
    ) {
      this.cardHeader = "Non-Provider Clinicians";
    } else if (this._contactType === CompanyResourceType.VOLUNTEER) {
      this.cardHeader = "Volunteers";
    }
  }

  get cardHeader(): string {
    return this._cardHeader;
  }

  set cardHeader(value: string) {
    this._cardHeader = value;
  }

  get hasNonProviderCliniciansEnabled(): boolean {
    return this._hasNonProviderCliniciansEnabled;
  }

  @Input()
  set hasNonProviderCliniciansEnabled(value: boolean) {
    this._hasNonProviderCliniciansEnabled = value;
  }

  get hasProviderEnabled(): boolean {
    return this._hasProviderEnabled;
  }

  get languages(): LookupValue[] {
    return this._languages;
  }

  @Input()
  set hasProviderEnabled(value: boolean) {
    this._hasProviderEnabled = value;
  }

  get hasVolunteersEnabled(): boolean {
    return this._hasVolunteersEnabled;
  }

  @Input()
  set professionalRelations(value: LookupValue[]) {
    this._professionalRelations = value;
  }

  get professionalRelations(): LookupValue[] {
    return this._professionalRelations;
  }

  @Input()
  set hasVolunteersEnabled(value: boolean) {
    this._hasVolunteersEnabled = value;
  }

  @Input()
  set languages(languages: LookupValue[]) {
    this._languages = languages;

    this._languageSelectItems = languages.map((x: LookupValue) => {
      return {
        name: x.description,
        value: x.description,
        checked: false,
      };
    });

    this.languageLookup = languages.reduce(function (map, obj) {
      map[obj.description] = obj.id;
      return map;
    }, {});
  }

  get credentialTypes(): LookupValue[] {
    return this._credentialTypes;
  }

  @Input()
  set credentialTypes(value: LookupValue[]) {
    this._credentialTypes = value;
  }

  get volunteerTypes(): LookupValue[] {
    return this._volunteerTypes;
  }

  @Input()
  set volunteerTypes(value: LookupValue[]) {
    this._volunteerTypes = value;
  }

  get volunteerActivityCategories(): any {
    return this._volunteerActivityCategories;
  }

  @Input()
  set volunteerActivityCategories(value: any) {
    this._volunteerActivityCategories = value;
  }

  get resourceIsVolunteer(): boolean {
    return this.contactType === CompanyResourceType.VOLUNTEER;
  }

  @Input()
  set volunteerMatchTerms(value: VolunteerMatchTerm[]) {
    this._volunteerMatchTerms = value;
  }

  get volunteerMatchTerms(): VolunteerMatchTerm[] {
    return this._volunteerMatchTerms;
  }

  get languageSelectItems(): any {
    return this._languageSelectItems;
  }

  get volunteerMatchTermItems(): any {
    if (
      !this._volunteerMatchTermItems &&
      this.volunteerMatchTerms &&
      this.volunteerMatchTerms.length > 0
    ) {
      this._volunteerMatchTermItems = this.volunteerMatchTerms.map(
        (x: VolunteerMatchTerm) => {
          return {
            name: x.term,
            value: x.term,
            checked: false,
          };
        }
      );
    }
    return this._volunteerMatchTermItems;
  }

  // endregion
}



// WEBPACK FOOTER //
// ./src/components/company-resources-grid/company-resources-grid.ts